import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Save, Plus, Edit2, AlertTriangle, X } from 'lucide-react';
import { clearPlatformSettingsCache } from '../hooks/usePlatformSettings';

const BORDER = '#e5e7eb';

function RefModal({ modal, onClose, onSave, saving }) {
  const isEdit = modal.mode === 'edit';
  const type = modal.type;

  const [form, setForm] = useState(() => {
    if (isEdit && modal.item) {
      return {
        slug: modal.item.slug || '',
        display_name: modal.item.display_name || '',
        description: modal.item.description || '',
        is_active: modal.item.is_active !== false,
      };
    }
    return { slug: '', display_name: '', description: '', is_active: true };
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const title = {
    industries: isEdit ? 'Edit Industry' : 'Add Industry',
    'business-sizes': isEdit ? 'Edit Business Size' : 'Add Business Size',
    categories: isEdit ? 'Edit Category' : 'Add Category',
  }[type];

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(10,26,15,0.45)', backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, boxShadow: '0 24px 64px rgba(0,0,0,0.18)', zIndex: 1, padding: '28px 28px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0a1a0f', fontFamily: 'Georgia, serif' }}>{title}</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', padding: 4 }}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 5 }}>Slug</label>
            <input
              value={form.slug}
              onChange={e => set('slug', e.target.value)}
              placeholder="e.g. restaurant"
              style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 5 }}>Display Name</label>
            <input
              value={form.display_name}
              onChange={e => set('display_name', e.target.value)}
              placeholder="e.g. Restaurant & Food Service"
              style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
          {type === 'categories' && (
            <div>
              <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 5 }}>Description</label>
              <input
                value={form.description}
                onChange={e => set('description', e.target.value)}
                placeholder="Brief description"
                style={{ width: '100%', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          )}
          {type !== 'categories' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#444', cursor: 'pointer' }}>
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} style={{ width: 16, height: 16 }} />
              Active
            </label>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
          <button onClick={onClose} disabled={saving} style={{ flex: 1, background: '#fff', color: '#888', border: `1.5px solid ${BORDER}`, padding: '10px', borderRadius: 999, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => onSave(form)} disabled={saving || !form.slug || !form.display_name} style={{ flex: 1, background: '#004D35', color: '#fff', border: 'none', padding: '10px', borderRadius: 999, fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Add')}
          </button>
        </div>
      </div>
    </div>
  );
}

const AdminSettings = () => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'general');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [settings, setSettings] = useState({
    platform_name: 'Viysr',
    support_email: 'support@viysr.com',
    from_email: 'noreply@viysr.com',
    beta_mode: true,
    allow_new_applications: true,
    allow_new_signups: true,
    enable_compare: true,
    enable_quiz: true,
    trial_length_days: 14,
    advisor_application_url: '',
  });

  const [industries, setIndustries] = useState([]);
  const [businessSizes, setBusinessSizes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);

  const [refModal, setRefModal] = useState(null);
  const [refSaving, setRefSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 2500); };

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchData();
  }, [user, activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'general') {
        const response = await fetch('/api/admin/settings', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setSettings(prev => ({ ...prev, ...data }));
        }
      } else if (activeTab === 'reference') {
        await fetchRefData();
      } else if (activeTab === 'activity') {
        const response = await fetch('/api/admin/activity-log', { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setActivityLogs(data);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRefData = async () => {
    const [indRes, sizeRes, catRes] = await Promise.all([
      fetch('/api/reference/industries', { credentials: 'include' }),
      fetch('/api/reference/business-sizes', { credentials: 'include' }),
      fetch('/api/reference/categories', { credentials: 'include' })
    ]);
    if (indRes.ok) setIndustries(await indRes.json());
    if (sizeRes.ok) setBusinessSizes(await sizeRes.json());
    if (catRes.ok) setCategories(await catRes.json());
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });

      if (response.ok) {
        clearPlatformSettingsCache();
        showToast('Settings saved successfully');
      } else {
        showToast('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showToast('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleRefSave = async (form) => {
    const { mode, type, item } = refModal;
    setRefSaving(true);
    try {
      const url = mode === 'add'
        ? `/api/reference/${type}`
        : `/api/reference/${type}/${item.id}`;
      const method = mode === 'add' ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        showToast(mode === 'add' ? 'Entry added successfully' : 'Entry updated successfully');
        setRefModal(null);
        await fetchRefData();
      } else {
        const err = await response.json().catch(() => ({}));
        showToast(err.error || 'Failed to save');
      }
    } catch (error) {
      console.error('Ref save error:', error);
      showToast('Failed to save');
    } finally {
      setRefSaving(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const renderGeneralTab = () => (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">PLATFORM</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Platform Name</label>
            <input
              type="text"
              value={settings.platform_name}
              onChange={(e) => setSettings({ ...settings, platform_name: e.target.value })}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Support Email</label>
            <input
              type="email"
              value={settings.support_email}
              onChange={(e) => setSettings({ ...settings, support_email: e.target.value })}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Email</label>
            <input
              type="email"
              value={settings.from_email}
              onChange={(e) => setSettings({ ...settings, from_email: e.target.value })}
              className="w-full max-w-md border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-1 uppercase tracking-wider">Advisor Application URL</h3>
        <p className="text-xs text-gray-500 mb-4">Used across the site wherever advisors are invited to apply.</p>
        <div className="max-w-lg">
          <label className="block text-sm font-medium text-gray-700 mb-1">Application URL</label>
          <input
            type="url"
            value={settings.advisor_application_url || ''}
            onChange={(e) => setSettings({ ...settings, advisor_application_url: e.target.value })}
            placeholder="https://form.typeform.com/to/..."
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />
          <p className="text-xs text-gray-400 mt-1">Used in: Get Started page, Login page, Nav bar, Advisor section CTA</p>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wider">Subscription Pricing</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 max-w-md space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Monthly Price</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-medium">$</span>
              <input
                type="number"
                value={settings.global_monthly_price || '0'}
                onChange={(e) => setSettings({ ...settings, global_monthly_price: e.target.value })}
                className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-lg text-sm bg-white"
              />
            </div>
            <p className="text-xs text-gray-500 mt-2 italic">
              Default rate for paid subscriptions. Currently $0 (Beta).
            </p>
          </div>

          <hr className="border-gray-200" />

          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-medium text-gray-900">Stripe Live Mode</span>
              <p className="text-xs text-gray-500">Charge real cards on the platform</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.stripe_live_mode || false}
                onChange={(e) => {
                  if (e.target.checked && !window.confirm("WARNING: Toggling LIVE MODE will process REAL transactions. Ensure Stripe keys are correct. Continue?")) return;
                  setSettings({ ...settings, stripe_live_mode: e.target.checked });
                }}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
          {settings.stripe_live_mode && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[10px] text-amber-800 leading-normal font-semibold">
                LIVE MODE ACTIVE: Real payments will be processed through the connected Stripe account.
              </p>
            </div>
          )}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">BETA MODE</h3>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <label className="flex items-start space-x-3">
            <input
              type="checkbox"
              checked={settings.beta_mode}
              onChange={(e) => setSettings({ ...settings, beta_mode: e.target.checked })}
              className="mt-1 rounded border-gray-300"
            />
            <div>
              <span className="font-medium text-gray-900">Beta Mode Active</span>
              <p className="text-sm text-gray-600 mt-1">When enabled:</p>
              <ul className="text-sm text-gray-600 list-disc list-inside mt-1">
                <li>All new advisors get free Beta subscriptions</li>
                <li>Stripe customers are created but not charged</li>
                <li>Beta badge shown on admin dashboard</li>
              </ul>
              {settings.beta_mode && (
                <div className="mt-3 p-3 bg-yellow-100 rounded-lg">
                  <p className="text-sm text-yellow-800 flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-1" />
                    Before turning off Beta mode:
                  </p>
                  <ul className="text-sm text-yellow-700 list-disc list-inside mt-1">
                    <li>Review all conversion plans in Subscriptions</li>
                    <li>Ensure Stripe webhooks are configured</li>
                    <li>Notify all Beta advisors of upcoming charges</li>
                  </ul>
                </div>
              )}
            </div>
          </label>
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">FEATURES</h3>
        <div className="space-y-3">
          <label className="flex items-center space-x-3">
            <input type="checkbox" checked={settings.allow_new_applications} onChange={(e) => setSettings({ ...settings, allow_new_applications: e.target.checked })} className="rounded border-gray-300" />
            <span className="text-sm text-gray-700">Allow new advisor applications</span>
          </label>
          <label className="flex items-center space-x-3">
            <input type="checkbox" checked={settings.allow_new_signups} onChange={(e) => setSettings({ ...settings, allow_new_signups: e.target.checked })} className="rounded border-gray-300" />
            <span className="text-sm text-gray-700">Allow new business owner signups</span>
          </label>
          <label className="flex items-center space-x-3">
            <input type="checkbox" checked={settings.enable_compare} onChange={(e) => setSettings({ ...settings, enable_compare: e.target.checked })} className="rounded border-gray-300" />
            <span className="text-sm text-gray-700">Enable compare feature</span>
          </label>
          <label className="flex items-center space-x-3">
            <input type="checkbox" checked={settings.enable_quiz} onChange={(e) => setSettings({ ...settings, enable_quiz: e.target.checked })} className="rounded border-gray-300" />
            <span className="text-sm text-gray-700">Enable quiz</span>
          </label>
        </div>
      </div>

      <div>
        <button
          onClick={handleSaveSettings}
          disabled={saving}
          className="px-6 py-2 bg-gray-900 hover:bg-gray-800 text-white text-sm font-medium rounded-lg flex items-center space-x-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Settings'}</span>
        </button>
      </div>
    </div>
  );

  const RefTable = ({ title, type, items, columns }) => (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <button
          onClick={() => setRefModal({ mode: 'add', type, item: null })}
          className="text-sm text-blue-600 hover:text-blue-800 flex items-center space-x-1"
        >
          <Plus className="w-4 h-4" />
          <span>Add</span>
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {columns.map(c => (
                <th key={c} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{c}</th>
              ))}
              <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {items.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-2 text-sm text-gray-900">{item.slug}</td>
                <td className="px-4 py-2 text-sm text-gray-600">{item.display_name}</td>
                {type === 'categories' ? (
                  <td className="px-4 py-2 text-sm text-gray-500 truncate max-w-xs">{item.description || '—'}</td>
                ) : (
                  <td className="px-4 py-2 text-sm">
                    <span className={`px-2 py-0.5 text-xs rounded-full ${item.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                      {item.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                )}
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setRefModal({ mode: 'edit', type, item })}
                    className="text-gray-400 hover:text-gray-700 transition-colors"
                    title="Edit"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400">No entries yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderReferenceTab = () => (
    <div className="space-y-8">
      <p className="text-sm text-gray-600">
        These tables power all dropdowns across the platform. Changes here immediately affect Quiz, Advisor Profiles, and Filters.
      </p>
      <RefTable title="INDUSTRIES" type="industries" items={industries} columns={['Slug', 'Display Name', 'Status']} />
      <RefTable title="BUSINESS SIZES" type="business-sizes" items={businessSizes} columns={['Slug', 'Display Name', 'Status']} />
      <RefTable title="ADVISOR CATEGORIES" type="categories" items={categories} columns={['Slug', 'Display Name', 'Description']} />
    </div>
  );

  const renderActivityTab = () => (
    <div className="space-y-4">
      <h3 className="text-sm font-semibold text-gray-900">ACTIVITY LOG</h3>
      {activityLogs.length === 0 ? (
        <p className="text-sm text-gray-500">No activity logs found</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {activityLogs.map((log) => (
                <tr key={log.id}>
                  <td className="px-4 py-2 text-sm text-gray-500">{formatDate(log.created_at)}</td>
                  <td className="px-4 py-2 text-sm text-gray-900">{log.admin_name || 'System'}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{log.action}</td>
                  <td className="px-4 py-2 text-sm text-gray-600">{log.entity_type} #{log.entity_id}</td>
                  <td className="px-4 py-2 text-sm text-gray-500 truncate max-w-xs">
                    {log.details ? JSON.stringify(log.details) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const handleExportDownload = async (endpoint, filename) => {
    try {
      const res = await fetch(endpoint, { credentials: 'include', headers: getAuthHeader() });
      if (!res.ok) { alert('Export failed. Please try again.'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed. Please try again.');
    }
  };

  const renderExportsTab = () => (
    <div className="space-y-6">
      <h3 className="text-sm font-semibold text-gray-900">CONSENT EXPORTS</h3>
      <p className="text-sm text-gray-500">Download CSV exports of consent and opt-in records collected on the platform.</p>
      <div className="space-y-4">
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-900">Advisor Consent Records</p>
            <p className="text-xs text-gray-500 mt-0.5">Terms accepted, email opt-in, and SMS opt-in at advisor onboarding</p>
          </div>
          <button
            onClick={() => handleExportDownload('/api/admin/consents/advisors', 'advisor-consents.csv')}
            className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Download CSV
          </button>
        </div>
        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
          <div>
            <p className="text-sm font-medium text-gray-900">Business Owner Consent Records</p>
            <p className="text-xs text-gray-500 mt-0.5">Terms accepted, email opt-in, and SMS opt-in at business owner sign-in</p>
          </div>
          <button
            onClick={() => handleExportDownload('/api/admin/consents/business-owners', 'business-owner-consents.csv')}
            className="px-4 py-2 text-sm font-medium bg-gray-900 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout activeTab="settings">
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', background: '#0a1a0f', color: '#fff', padding: '12px 28px', borderRadius: 999, fontSize: 14, zIndex: 600, boxShadow: '0 8px 32px rgba(0,0,0,0.2)', whiteSpace: 'nowrap' }}>
          {toast}
        </div>
      )}

      {refModal && (
        <RefModal
          modal={refModal}
          onClose={() => setRefModal(null)}
          onSave={handleRefSave}
          saving={refSaving}
        />
      )}

      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>

        <div className="flex items-center space-x-2">
          {[
            { key: 'general', label: 'General' },
            { key: 'reference', label: 'Reference Data' },
            { key: 'activity', label: 'Activity Log' },
            { key: 'exports', label: 'Exports' }
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeTab === tab.key
                  ? 'bg-gray-900 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <>
              {activeTab === 'general' && renderGeneralTab()}
              {activeTab === 'reference' && renderReferenceTab()}
              {activeTab === 'activity' && renderActivityTab()}
              {activeTab === 'exports' && renderExportsTab()}
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
